import { useEditorStore, ElementLayer } from '@/store/editor-store'
import { ArrowUpRight01Icon, SquareIcon, CircleIcon, Message01Icon as MessageCircle, Delete01Icon as Trash2 } from 'hugeicons-react'
export default function ElementControls() {
    const { elementLayers, selectedElementLayerId, updateElementLayer, removeElementLayer, addElementLayer, currentTime } = useEditorStore()

    const activeElement = elementLayers.find(e => e.id === selectedElementLayerId)

    const handleAddElement = (type: ElementLayer['type']) => {
        addElementLayer({
            id: crypto.randomUUID(),
            type,
            startTime: currentTime,
            endTime: currentTime + 3,
            x: 50,
            y: 50,
            width: type === 'arrow' ? 20 : 30,
            height: type === 'arrow' ? 2 : 20,
            color: '#ef4444', // Red by default
            strokeWidth: 4,
            rotation: type === 'arrow' ? -45 : 0,
            opacity: 100,
            content: type === 'callout' ? 'Callout text' : undefined,
            fontSize: type === 'callout' ? 24 : undefined,
            fontFamily: type === 'callout' ? 'Inter' : undefined,
            colorText: type === 'callout' ? '#ffffff' : undefined,
        })
    }

    return (
        <div className="space-y-6">
            <div>
                <h3 className="text-[13px] font-semibold text-white mb-3">Add Element</h3>
                <div className="grid grid-cols-2 gap-2">
                    <button
                        onClick={() => handleAddElement('arrow')}
                        className="flex items-center gap-2 bg-surface-700/50 hover:bg-surface-600 border border-white/5 p-3 rounded-xl text-sm font-medium transition-colors"
                    >
                        <ArrowUpRight01Icon size={16} /> Arrow
                    </button>
                    <button
                        onClick={() => handleAddElement('rectangle')}
                        className="flex items-center gap-2 bg-surface-700/50 hover:bg-surface-600 border border-white/5 p-3 rounded-xl text-sm font-medium transition-colors"
                    >
                        <SquareIcon size={16} /> Rectangle
                    </button>
                    <button
                        onClick={() => handleAddElement('ellipse')}
                        className="flex items-center gap-2 bg-surface-700/50 hover:bg-surface-600 border border-white/5 p-3 rounded-xl text-sm font-medium transition-colors"
                    >
                        <CircleIcon size={16} /> Ellipse
                    </button>
                    <button
                        onClick={() => handleAddElement('callout')}
                        className="flex items-center gap-2 bg-surface-700/50 hover:bg-surface-600 border border-white/5 p-3 rounded-xl text-sm font-medium transition-colors"
                    >
                        <MessageCircle size={16} /> Callout
                    </button>
                </div>
            </div>

            {activeElement ? (
                <div className="bg-surface-800/80 border border-white/5 rounded-2xl p-4 space-y-5 shadow-sm">
                    <div className="flex items-center justify-between">
                        <span className="text-xs font-semibold text-brand-400 bg-brand-400/10 px-2 py-1 rounded capitalize">
                            {activeElement.type} Settings
                        </span>
                        <button
                            onClick={() => removeElementLayer(activeElement.id)}
                            className="p-1.5 hover:bg-red-500/20 text-txt-secondary hover:text-red-400 rounded-lg transition-colors"
                        >
                            <Trash2 size={16} />
                        </button>
                    </div>

                    <div className="space-y-4">
                        {/* Position */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-txt-secondary mb-1.5 block">X Position (%)</label>
                                <input
                                    type="number"
                                    value={activeElement.x}
                                    onChange={(e) => updateElementLayer(activeElement.id, { x: Number(e.target.value) })}
                                    className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-txt-secondary mb-1.5 block">Y Position (%)</label>
                                <input
                                    type="number"
                                    value={activeElement.y}
                                    onChange={(e) => updateElementLayer(activeElement.id, { y: Number(e.target.value) })}
                                    className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                />
                            </div>
                        </div>

                        {/* Size */}
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <label className="text-xs text-txt-secondary mb-1.5 block">Width (%)</label>
                                <input
                                    type="number"
                                    value={activeElement.width}
                                    onChange={(e) => updateElementLayer(activeElement.id, { width: Number(e.target.value) })}
                                    className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                />
                            </div>
                            <div>
                                <label className="text-xs text-txt-secondary mb-1.5 block">Height (%)</label>
                                <input
                                    type="number"
                                    value={activeElement.height}
                                    onChange={(e) => updateElementLayer(activeElement.id, { height: Number(e.target.value) })}
                                    className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                />
                            </div>
                        </div>

                        {/* Rotation */}
                        <div>
                            <label className="text-xs text-txt-secondary mb-1.5 block">Rotation (deg)</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    min="-180"
                                    max="180"
                                    value={activeElement.rotation}
                                    onChange={(e) => updateElementLayer(activeElement.id, { rotation: Number(e.target.value) })}
                                    className="flex-1 accent-brand-500"
                                />
                                <span className="text-xs font-medium w-12 text-right">{activeElement.rotation}°</span>
                            </div>
                        </div>

                        {/* Color */}
                        <div>
                            <label className="text-xs text-txt-secondary mb-1.5 block">Color</label>
                            <div className="flex gap-2 items-center">
                                <input
                                    type="color"
                                    value={activeElement.color}
                                    onChange={(e) => updateElementLayer(activeElement.id, { color: e.target.value })}
                                    className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0"
                                />
                                <input
                                    type="text"
                                    value={activeElement.color}
                                    onChange={(e) => updateElementLayer(activeElement.id, { color: e.target.value })}
                                    className="flex-1 bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm uppercase"
                                />
                            </div>
                        </div>
                        
                        {/* Stroke Width */}
                        <div>
                            <label className="text-xs text-txt-secondary mb-1.5 block">Stroke Width</label>
                            <div className="flex items-center gap-3">
                                <input
                                    type="range"
                                    min="1"
                                    max="20"
                                    value={activeElement.strokeWidth}
                                    onChange={(e) => updateElementLayer(activeElement.id, { strokeWidth: Number(e.target.value) })}
                                    className="flex-1 accent-brand-500"
                                />
                                <span className="text-xs font-medium w-8 text-right">{activeElement.strokeWidth}</span>
                            </div>
                        </div>

                        {/* Callout Specific Controls */}
                        {activeElement.type === 'callout' && (
                            <div className="pt-4 border-t border-white/5 space-y-4">
                                <div>
                                    <label className="text-xs text-txt-secondary mb-1.5 block">Text Content</label>
                                    <input
                                        type="text"
                                        value={activeElement.content || ''}
                                        onChange={(e) => updateElementLayer(activeElement.id, { content: e.target.value })}
                                        className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                    />
                                </div>
                                <div className="grid grid-cols-2 gap-3">
                                    <div>
                                        <label className="text-xs text-txt-secondary mb-1.5 block">Font Size</label>
                                        <input
                                            type="number"
                                            value={activeElement.fontSize || 24}
                                            onChange={(e) => updateElementLayer(activeElement.id, { fontSize: Number(e.target.value) })}
                                            className="w-full bg-surface-900 border border-white/10 rounded-lg px-3 py-1.5 text-sm"
                                        />
                                    </div>
                                    <div>
                                        <label className="text-xs text-txt-secondary mb-1.5 block">Text Color</label>
                                        <div className="flex gap-2 items-center">
                                            <input
                                                type="color"
                                                value={activeElement.colorText || '#ffffff'}
                                                onChange={(e) => updateElementLayer(activeElement.id, { colorText: e.target.value })}
                                                className="w-8 h-8 rounded cursor-pointer bg-transparent border-0 p-0"
                                            />
                                        </div>
                                    </div>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div className="text-center py-8">
                    <p className="text-sm text-txt-secondary">
                        Click an element in the timeline or preview to edit its properties.
                    </p>
                </div>
            )}
        </div>
    )
}
